import { config } from "../Environment";
import { OutlineItem } from "../components/designView/outliner/types";

// ============================================
// Breakdown Action Item (Sub-tasks)
// ============================================

export interface BreakdownActionRequest {
  actionItemId: string;
  content: string;
  category?: string;
  language: string;
}

export interface BreakdownSuggestion {
  content: string;
  estimatedTime?: string;
  priority?: "high" | "medium" | "low";
}

export interface BreakdownActionResponse {
  status: "SUCCESS" | "ERROR";
  suggestions: BreakdownSuggestion[];
  confidence: number;
  fault?: string;
}

/**
 * Get AI-suggested breakdown of an action item into sub-tasks
 */
export const breakdownAction = async (
  retryFetch: Function,
  data: BreakdownActionRequest
): Promise<BreakdownActionResponse> => {
  try {
    const response = await retryFetch(`${config.API_BASE}/api/ai/breakdown-action`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      // Return local fallback
      return generateLocalBreakdown(data.content, data.language);
    }

    return await response.json();
  } catch (error) {
    console.warn("AI breakdown unavailable, using local fallback:", error);
    return generateLocalBreakdown(data.content, data.language);
  }
};

/**
 * Local fallback for breakdown suggestions when API is unavailable
 */
function generateLocalBreakdown(content: string, language: string): BreakdownActionResponse {
  const suggestions: BreakdownSuggestion[] = [];

  // Generic breakdown in the appropriate language
  if (language === "he") {
    suggestions.push(
      { content: "להגדיר איך נראית הצלחה", estimatedTime: "10 דק׳" },
      { content: "לזהות את הצעד הראשון הקונקרטי", estimatedTime: "10 דק׳" },
      { content: "לאסוף משאבים נדרשים", estimatedTime: "20 דק׳" },
      { content: "לבצע את המשימה העיקרית", estimatedTime: "משתנה" },
      { content: "לסקור ולעקוב", estimatedTime: "15 דק׳" }
    );
  } else if (language === "de") {
    suggestions.push(
      { content: "Definieren, wie Erfolg aussieht", estimatedTime: "10 Min" },
      { content: "Ersten konkreten Schritt identifizieren", estimatedTime: "10 Min" },
      { content: "Notwendige Ressourcen sammeln", estimatedTime: "20 Min" },
      { content: "Hauptaufgabe durchführen", estimatedTime: "variiert" },
      { content: "Überprüfen und nachverfolgen", estimatedTime: "15 Min" }
    );
  } else {
    suggestions.push(
      { content: "Define what success looks like", estimatedTime: "10 min" },
      { content: "Identify first concrete step", estimatedTime: "10 min" },
      { content: "Gather necessary resources", estimatedTime: "20 min" },
      { content: "Complete the main task", estimatedTime: "varies" },
      { content: "Review and follow up", estimatedTime: "15 min" }
    );
  }

  return {
    status: "SUCCESS",
    suggestions,
    confidence: 0.7,
  };
}

// ============================================
// Action to Activity Outline
// ============================================

export interface ActionToOutlineRequest {
  actionItemId: string;
  content: string;
  category?: string;
  language: string;
  subtasks?: string[];
  context?: {
    relatedInsights?: string[];
  };
}

export interface ActionToOutlineResponse {
  status: "SUCCESS" | "ERROR";
  suggestedTitle: string;
  outline: OutlineItem[];
  estimatedPanes: number;
  estimatedDuration: string;
  confidence: number;
  fault?: string;
}

/**
 * Get AI-suggested activity outline from an action item
 */
export const actionToOutline = async (
  retryFetch: Function,
  data: ActionToOutlineRequest
): Promise<ActionToOutlineResponse> => {
  try {
    const response = await retryFetch(`${config.API_BASE}/api/ai/action-to-outline`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      return generateLocalOutline(data.content, data.language);
    }

    return await response.json();
  } catch (error) {
    console.warn("AI outline generation unavailable, using local fallback:", error);
    return generateLocalOutline(data.content, data.language);
  }
};

/**
 * Local fallback for outline generation when API is unavailable
 */
function generateLocalOutline(content: string, language: string): ActionToOutlineResponse {
  const generateId = () => `out_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  let title = content;
  if (content.length > 50) {
    title = content.substring(0, 47) + "...";
  }

  let outline: OutlineItem[];

  if (language === "he") {
    outline = [
      {
        id: generateId(),
        text: "התחלה",
        children: [
          { id: generateId(), text: "סקירה ומטרות", children: [], expanded: true },
          { id: generateId(), text: "מה תצטרכו", children: [], expanded: true },
        ],
        expanded: true,
      },
      {
        id: generateId(),
        text: "תוכן מרכזי",
        children: [
          { id: generateId(), text: "מושגי יסוד", children: [], expanded: true },
          { id: generateId(), text: "צעדים מעשיים", children: [], expanded: true },
        ],
        expanded: true,
      },
      {
        id: generateId(),
        text: "סיכום",
        children: [
          { id: generateId(), text: "תקציר", children: [], expanded: true },
          { id: generateId(), text: "צעדים הבאים", children: [], expanded: true },
        ],
        expanded: true,
      },
    ];
  } else if (language === "de") {
    outline = [
      {
        id: generateId(),
        text: "Einstieg",
        children: [
          { id: generateId(), text: "Überblick und Ziele", children: [], expanded: true },
          { id: generateId(), text: "Was Sie brauchen", children: [], expanded: true },
        ],
        expanded: true,
      },
      {
        id: generateId(),
        text: "Hauptinhalt",
        children: [
          { id: generateId(), text: "Kernkonzepte", children: [], expanded: true },
          { id: generateId(), text: "Praktische Schritte", children: [], expanded: true },
        ],
        expanded: true,
      },
      {
        id: generateId(),
        text: "Abschluss",
        children: [
          { id: generateId(), text: "Zusammenfassung", children: [], expanded: true },
          { id: generateId(), text: "Nächste Schritte", children: [], expanded: true },
        ],
        expanded: true,
      },
    ];
  } else {
    outline = [
      {
        id: generateId(),
        text: "Getting Started",
        children: [
          { id: generateId(), text: "Overview and goals", children: [], expanded: true },
          { id: generateId(), text: "What you'll need", children: [], expanded: true },
        ],
        expanded: true,
      },
      {
        id: generateId(),
        text: "Main Content",
        children: [
          { id: generateId(), text: "Core concepts", children: [], expanded: true },
          { id: generateId(), text: "Practical steps", children: [], expanded: true },
        ],
        expanded: true,
      },
      {
        id: generateId(),
        text: "Wrap Up",
        children: [
          { id: generateId(), text: "Summary", children: [], expanded: true },
          { id: generateId(), text: "Next steps", children: [], expanded: true },
        ],
        expanded: true,
      },
    ];
  }

  const durationLabel = language === "he" ? "דקות" : language === "de" ? "Minuten" : "minutes";

  return {
    status: "SUCCESS",
    suggestedTitle: title,
    outline,
    estimatedPanes: outline.length,
    estimatedDuration: `${outline.length * 3}-${outline.length * 5} ${durationLabel}`,
    confidence: 0.7,
  };
}

// ============================================
// Create Activity from Outline
// ============================================

export interface CreateActivityFromOutlineRequest {
  title: string;
  outline: OutlineItem[];
  sourceActionItemId: string;
  settings: {
    language: string;
    tone?: string;
    generateComponents: boolean;
    markActionComplete?: boolean;
  };
}

export interface CreateActivityFromOutlineResponse {
  status: "SUCCESS" | "ERROR";
  prescriptionId: string;
  prescriptionName: string;
  panesCreated: number;
  componentsGenerated: number;
  fault?: string;
}

/**
 * Create an activity from an outline
 */
export const createActivityFromOutline = async (
  retryFetch: Function,
  data: CreateActivityFromOutlineRequest
): Promise<CreateActivityFromOutlineResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/ai/create-activity-from-outline`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create activity: ${response.statusText}`);
  }

  return await response.json();
};

// ============================================
// Quick Generate Activity
// ============================================

export interface QuickGenerateActivityRequest {
  actionItemId: string;
  content: string;
  language: string;
  settings?: {
    tone?: string;
    complexity?: "simple" | "moderate" | "detailed";
  };
}

export interface QuickGenerateActivityResponse {
  status: "SUCCESS" | "ERROR";
  prescriptionId: string;
  prescriptionName: string;
  panesCreated: number;
  fault?: string;
}

/**
 * Quick generate a complete activity from an action item
 */
export const quickGenerateActivity = async (
  retryFetch: Function,
  data: QuickGenerateActivityRequest
): Promise<QuickGenerateActivityResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/ai/quick-generate-activity`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to generate activity: ${response.statusText}`);
  }

  return await response.json();
};

// ============================================
// Create Sub-tasks
// ============================================

export interface CreateSubtasksRequest {
  parentId: string;
  subtasks: Array<{
    content: string;
    priority?: "high" | "medium" | "low";
    dueDate?: number;
  }>;
  inheritParentSettings?: boolean;
}

export interface CreateSubtasksResponse {
  status: "SUCCESS" | "ERROR";
  created: number;
  parentUpdated: boolean;
  fault?: string;
}

/**
 * Create sub-tasks for an action item
 */
export const createSubtasks = async (
  retryFetch: Function,
  data: CreateSubtasksRequest
): Promise<CreateSubtasksResponse> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-takeaway/create-subtasks`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create subtasks: ${response.statusText}`);
  }

  return await response.json();
};
